import React, { useState, useEffect } from 'react';
import { Property } from '../types/Property';
import { Booking } from './BookingSystem';
import PropertyListingForm, { PropertyFormData } from './PropertyListingForm';
import './HostDashboard.css';
import { mintPropertyNft, makeDataUriJson } from '../utils/nft';

interface HostDashboardProps {
  userEmail: string;
  userWallet: string;
}

const HostDashboard: React.FC<HostDashboardProps> = ({ userEmail, userWallet }) => {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'properties' | 'bookings' | 'list-property'>('dashboard');
  const [hostProperties, setHostProperties] = useState<Property[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [showListingForm, setShowListingForm] = useState(false);
  const [stats, setStats] = useState({
    totalProperties: 0,
    totalBookings: 0,
    totalEarnings: 0,
    pendingBookings: 0
  });
  const [availabilityProperty, setAvailabilityProperty] = useState<Property | null>(null);
  const [blockedDates, setBlockedDates] = useState<string[]>([]);
  const [chatBooking, setChatBooking] = useState<Booking | null>(null);
  const [chatMessages, setChatMessages] = useState<Array<{ sender: string; text: string; at: number }>>([]);

  useEffect(() => {
    // Load host data immediately
    loadHostData();
    
    // Also listen for storage changes (in case bookings are added from another tab/window)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'ethereus_bookings') {
        loadHostData();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    
    // Listen for booking updates from guest bookings
    const handleBookingUpdate = () => {
      loadHostData();
    };
    window.addEventListener('afg:bookings-updated', handleBookingUpdate);
    
    // Listen for booking updates across tabs via BroadcastChannel
    let bookingChannel: any;
    try {
      bookingChannel = new BroadcastChannel('afg-bookings');
      bookingChannel.onmessage = (ev: any) => {
        const data = ev.data || {};
        if (data.type === 'booking-created' || data.type === 'booking-updated') {
          loadHostData();
        }
      };
    } catch {}
    
    // Real-time chat sync across tabs
    let ch: any;
    try {
      ch = new (window as any).BroadcastChannel('afg-chat');
      ch.onmessage = (ev: any) => {
        const data = ev.data || {};
        // Skip if it's a local message (from same tab) to prevent duplication
        if (data.isLocal) return;
        if (data.type === 'chat' && chatBooking) {
          const key = `afg_chat_${chatBooking.id}`;
          const legacyKey = `afg_chat_${chatBooking.id}`;
          if (data.bookingKey === key || data.bookingKey === legacyKey) {
            // Reload from localStorage (which has the latest) and deduplicate
            const a = JSON.parse(localStorage.getItem(key) || '[]');
            const b = JSON.parse(localStorage.getItem(legacyKey) || '[]');
            // Merge and deduplicate
            const all = [...a, ...b];
            const seen = new Set<string>();
            const deduped = all.filter((m: any) => {
              const id = `${m.sender}-${m.text}-${Math.floor((m.at||0)/1000)}`;
              if (seen.has(id)) return false;
              seen.add(id);
              return true;
            }).sort((x: any, y: any) => (x.at||0) - (y.at||0));
            setChatMessages(deduped);
          }
        }
      };
    } catch {}
    
    return () => {
      window.removeEventListener('afg:bookings-updated', handleBookingUpdate);
      window.removeEventListener('storage', handleStorageChange);
      try { bookingChannel && bookingChannel.close && bookingChannel.close(); } catch {}
      try { ch && ch.close && ch.close(); } catch {}
    };
}, [userEmail, userWallet]);

  const loadHostData = () => {
  const hostId = (userEmail || userWallet || '').toLowerCase();
  const perHostKey = `afg_host_properties_${hostId}`;
  const perHostList = JSON.parse(localStorage.getItem(perHostKey) || '[]');
  const legacyAll = JSON.parse(localStorage.getItem('ethereus_host_properties') || '[]');
  const legacyMine = legacyAll.filter((prop: any) => (prop.hostEmail && prop.hostEmail === userEmail) || (prop.hostWallet && prop.hostWallet?.toLowerCase() === (userWallet||'').toLowerCase()));
  const mergedMap: Record<string, any> = {};
  [...perHostList, ...legacyMine].forEach((p: any) => { mergedMap[p.id] = p; });
  
  // Also check all per-host keys to find additional properties
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i) as string;
      if (k && k.startsWith('afg_host_properties_')) {
        try {
          const arr = JSON.parse(localStorage.getItem(k) || '[]');
          arr.forEach((p: any) => {
            const matchesEmail = userEmail && p.hostEmail === userEmail;
            const matchesWallet = userWallet && p.hostWallet && p.hostWallet.toLowerCase() === userWallet.toLowerCase();
            if (matchesEmail || matchesWallet) {
              mergedMap[p.id] = p;
            }
          });
        } catch {}
      }
    }
  } catch {}
  
  const userProperties = Object.values(mergedMap);
  setHostProperties(userProperties as Property[]);

    // Load bookings for host properties - check BOTH ethereus_bookings AND booking NFTs
    const storedBookings = JSON.parse(localStorage.getItem('ethereus_bookings') || '[]');
    
    // ALSO check booking NFTs as a source (guests might have NFTs even if booking record is missing)
    const bookingNFTs = JSON.parse(localStorage.getItem('ethereus_booking_nfts') || '[]');
    
    // Convert booking NFTs to bookings format (if not already in storedBookings)
    // This ensures hosts can see bookings even if only the NFT exists
    const bookingsFromNFTs = bookingNFTs.map((nft: any) => {
      // Check if this NFT already has a corresponding booking (match by bookingId)
      // Only skip if we find an exact bookingId match - don't skip based on other criteria
      const existingBooking = storedBookings.find((b: any) => {
        if (nft.bookingId && b.id === nft.bookingId) return true;
        return false;
      });
      if (existingBooking) {
        console.log('[HostDashboard] Skipping NFT conversion - booking already exists:', nft.id, 'bookingId:', nft.bookingId);
        return null; // Skip if already exists
      }
      
      // Only create booking if NFT has required fields (propertyId is critical)
      if (!nft.propertyId) {
        console.log('[HostDashboard] Skipping NFT - missing propertyId:', nft.id);
        return null;
      }
      
      // Create booking from NFT - include ALL NFT data
      const bookingFromNFT = {
        id: nft.bookingId || nft.id || `booking_from_nft_${Date.now()}_${nft.id}`,
        propertyId: String(nft.propertyId).trim(), // Ensure it's a string and trimmed
        propertyName: nft.propertyName || '',
        guestEmail: nft.guestEmail || '',
        guestName: nft.guestName || '',
        guestWallet: nft.ownerAddress || '',
        hostEmail: '', // Will be matched by property
        hostWallet: '', // Will be matched by property
        startDate: nft.startDate || nft.purchaseDate || new Date().toISOString(),
        endDate: nft.endDate || nft.purchaseDate || new Date().toISOString(),
        totalPrice: nft.totalPrice || nft.purchasePrice || 0,
        status: 'confirmed',
        createdAt: nft.purchaseDate || new Date().toISOString(),
        contactDetails: {}
      };
      
      console.log('[HostDashboard] Created booking from NFT:', bookingFromNFT.id, 'propertyId:', bookingFromNFT.propertyId);
      return bookingFromNFT;
    }).filter((b: any) => b !== null && b.propertyId); // Remove nulls and ensure propertyId exists
    
    // Merge both sources
    const allBookings = [...storedBookings, ...bookingsFromNFTs];
    
  const propertyIds = new Set(userProperties.map((p: Property) => String(p.id).trim()));
  
  // DEBUG: Log to help diagnose - ALWAYS log to fix this issue
  console.log('🔍 [HostDashboard DEBUG] ========================================');
  console.log('📋 [HostDashboard] User Properties Count:', userProperties.length);
  console.log('📋 [HostDashboard] User Properties:', userProperties.map(p => ({ id: p.id, name: p.name, hostEmail: p.hostEmail, hostWallet: p.hostWallet })));
  console.log('📋 [HostDashboard] Property IDs:', userProperties.map(p => p.id));
  console.log('📦 [HostDashboard] Stored Bookings Count:', storedBookings.length);
  console.log('📦 [HostDashboard] Stored Bookings:', storedBookings.map((b: Booking) => ({ id: b.id, propertyId: b.propertyId, propertyName: b.propertyName, hostEmail: b.hostEmail, hostWallet: b.hostWallet })));
  console.log('🎫 [HostDashboard] Booking NFTs Count:', bookingNFTs.length);
  console.log('🎫 [HostDashboard] Booking NFTs:', bookingNFTs.map((nft: any) => ({ id: nft.id, bookingId: nft.bookingId, propertyId: nft.propertyId, propertyName: nft.propertyName, ownerAddress: nft.ownerAddress })));
  console.log('🔄 [HostDashboard] Bookings Created from NFTs Count:', bookingsFromNFTs.length);
  console.log('🔄 [HostDashboard] Bookings from NFTs:', bookingsFromNFTs.map((b: any) => ({ id: b.id, propertyId: b.propertyId, propertyName: b.propertyName })));
  console.log('📊 [HostDashboard] All Bookings (merged) Count:', allBookings.length);
  console.log('📊 [HostDashboard] All Bookings (merged):', allBookings.map((b: Booking) => ({ id: b.id, propertyId: b.propertyId, propertyName: b.propertyName })));
  console.log('🔍 [HostDashboard DEBUG] ========================================');
  
  // Create a map of property host info for matching
  const propertyHostMap: Record<string, { email?: string; wallet?: string }> = {};
  userProperties.forEach((p: Property) => {
    propertyHostMap[String(p.id).trim()] = { email: p.hostEmail, wallet: p.hostWallet };
  });
  
  // Filter bookings: by host email/wallet OR by property ownership OR by property host info
  // Use DIRECT property array comparison instead of Set for maximum compatibility
  const hostBookings = allBookings.filter((booking: Booking) => {
    const bookingPropertyId = String(booking.propertyId || '').trim();
    const bookingPropertyName = String(booking.propertyName || '').trim().toLowerCase();
    
    // ULTRA-AGGRESSIVE: First try exact matches on propertyId in every possible format
    for (const p of userProperties) {
      const propId = String(p.id || '').trim();
      const propIdRaw = p.id;
      
      // Try all possible comparisons
      if (propId === bookingPropertyId ||
          propId === String(booking.propertyId) ||
          String(propIdRaw) === bookingPropertyId ||
          String(propIdRaw) === String(booking.propertyId) ||
          propIdRaw === booking.propertyId ||
          booking.propertyId === propIdRaw ||
          propId.toLowerCase() === bookingPropertyId.toLowerCase()) {
        console.log('[HostDashboard MATCH ✓] Booking matched by propertyId:', booking.id, 'propertyId:', bookingPropertyId, 'matches property:', p.id);
        return true;
      }
    }
    
    // FALLBACK: Match by property name (in case propertyId format differs but name matches)
    const propertyNameMatch = userProperties.some((p: Property) => {
      const propName = String(p.name || '').trim().toLowerCase();
      return propName === bookingPropertyName && propName.length > 0;
    });
    if (propertyNameMatch) {
      console.log('[HostDashboard MATCH] Booking matched by property name:', booking.id, 'propertyName:', bookingPropertyName);
      return true;
    }
    
    // Direct match on booking host fields (secondary check)
    const emailMatch = userEmail && booking.hostEmail === userEmail;
    const walletMatch = userWallet && booking.hostWallet && booking.hostWallet.toLowerCase() === userWallet.toLowerCase();
    
    // Match by property's host info (in case booking host fields don't match but property does)
    const propHostInfo = propertyHostMap[bookingPropertyId];
    const propEmailMatch = propHostInfo && userEmail && propHostInfo.email === userEmail;
    const propWalletMatch = propHostInfo && userWallet && propHostInfo.wallet && propHostInfo.wallet.toLowerCase() === userWallet.toLowerCase();
    
    // Check if propertyId was already matched in the loop above
    const propertyIdMatched = userProperties.some((p: Property) => {
      const propId = String(p.id || '').trim();
      return propId === bookingPropertyId || String(p.id) === String(booking.propertyId);
    });
    
    // If none of the above match, but we have a propertyId, check if it could belong to this host
    // This is a fallback to catch bookings that might have been created before host info was stored properly
    let fallbackMatch = false;
    if (!emailMatch && !walletMatch && !propertyIdMatched && !propertyNameMatch && !propEmailMatch && !propWalletMatch && bookingPropertyId) {
      // Check ALL properties in localStorage (not just userProperties) to see if this propertyId belongs to the host
      try {
        // Check legacy global list
        const legacyAll = JSON.parse(localStorage.getItem('ethereus_host_properties') || '[]');
        const legacyProp = legacyAll.find((p: any) => {
          const propId = String(p.id || '').trim();
          const propName = String(p.name || '').trim().toLowerCase();
          return propId === bookingPropertyId || (propName === bookingPropertyName && propName.length > 0);
        });
        if (legacyProp) {
          const legacyEmailMatch = userEmail && legacyProp.hostEmail === userEmail;
          const legacyWalletMatch = userWallet && legacyProp.hostWallet && legacyProp.hostWallet.toLowerCase() === userWallet.toLowerCase();
          if (legacyEmailMatch || legacyWalletMatch) {
            fallbackMatch = true;
          }
        }
        // Check all per-host keys
        if (!fallbackMatch) {
          for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i) as string;
            if (k && k.startsWith('afg_host_properties_')) {
              try {
                const arr = JSON.parse(localStorage.getItem(k) || '[]');
                const prop = arr.find((p: any) => {
                  const propId = String(p.id || '').trim();
                  const propName = String(p.name || '').trim().toLowerCase();
                  return propId === bookingPropertyId || (propName === bookingPropertyName && propName.length > 0);
                });
                if (prop) {
                  const propEmailMatch = userEmail && prop.hostEmail === userEmail;
                  const propWalletMatch = userWallet && prop.hostWallet && prop.hostWallet.toLowerCase() === userWallet.toLowerCase();
                  if (propEmailMatch || propWalletMatch) {
                    fallbackMatch = true;
                    break;
                  }
                }
              } catch {}
            }
          }
        }
      } catch {}
    }
    
    const finalMatch = emailMatch || walletMatch || propertyNameMatch || propEmailMatch || propWalletMatch || fallbackMatch;
    
    if (!finalMatch) {
      console.warn('[HostDashboard ❌ NO MATCH] Booking NOT matched:', {
        bookingId: booking.id,
        bookingPropertyId: bookingPropertyId,
        bookingPropertyName: bookingPropertyName,
        bookingSource: booking.id.includes('_nft_') ? 'NFT' : 'booking record',
        hostEmail: booking.hostEmail,
        hostWallet: booking.hostWallet,
        userEmail: userEmail,
        userWallet: userWallet,
        userPropertyIds: userProperties.map(p => p.id),
        userPropertyNames: userProperties.map(p => p.name)
      });
    } else {
      console.log('[HostDashboard ✓ FINAL MATCH] Booking matched:', booking.id, 'by:', {
        emailMatch, walletMatch, propertyNameMatch, propEmailMatch, propWalletMatch, fallbackMatch
      });
    }
    
    return finalMatch;
  });
    setBookings(hostBookings);

    // Calculate stats (ensure earnings include all bookings tied to this host's properties)
  // Create property host map for stats calculation too
  const propertyHostMapForStats: Record<string, { email?: string; wallet?: string }> = {};
  userProperties.forEach((p: Property) => {
    propertyHostMapForStats[String(p.id).trim()] = { email: p.hostEmail, wallet: p.hostWallet };
  });
  
  const eligibleBookings = allBookings.filter((b: Booking) => {
    if (b.status !== 'confirmed') return false;
    
    const bookingPropertyId = String(b.propertyId || '').trim();
    
    // Direct match on booking host fields
    const emailMatch = userEmail && b.hostEmail === userEmail;
    const walletMatch = userWallet && b.hostWallet && b.hostWallet.toLowerCase() === userWallet.toLowerCase();
    const propertyMatch = propertyIds.has(bookingPropertyId);
    
    // CRITICAL: If propertyId matches, include it regardless of host fields
    if (propertyMatch) {
      return true;
    }
    
    // Match by property's host info
    const propHostInfo = propertyHostMapForStats[bookingPropertyId];
    const propEmailMatch = propHostInfo && userEmail && propHostInfo.email === userEmail;
    const propWalletMatch = propHostInfo && userWallet && propHostInfo.wallet && propHostInfo.wallet.toLowerCase() === userWallet.toLowerCase();
    
    // Fallback: check all properties in localStorage
    let fallbackMatch = false;
    if (!emailMatch && !walletMatch && !propertyMatch && !propEmailMatch && !propWalletMatch && bookingPropertyId) {
      try {
        const legacyAll = JSON.parse(localStorage.getItem('ethereus_host_properties') || '[]');
        const legacyProp = legacyAll.find((p: any) => String(p.id).trim() === bookingPropertyId);
        if (legacyProp) {
          const legacyEmailMatch = userEmail && legacyProp.hostEmail === userEmail;
          const legacyWalletMatch = userWallet && legacyProp.hostWallet && legacyProp.hostWallet.toLowerCase() === userWallet.toLowerCase();
          if (legacyEmailMatch || legacyWalletMatch) {
            fallbackMatch = true;
          }
        }
        if (!fallbackMatch) {
          for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i) as string;
            if (k && k.startsWith('afg_host_properties_')) {
              try {
                const arr = JSON.parse(localStorage.getItem(k) || '[]');
                const prop = arr.find((p: any) => String(p.id).trim() === bookingPropertyId);
                if (prop) {
                  const propEmailMatch = userEmail && prop.hostEmail === userEmail;
                  const propWalletMatch = userWallet && prop.hostWallet && prop.hostWallet.toLowerCase() === userWallet.toLowerCase();
                  if (propEmailMatch || propWalletMatch) {
                    fallbackMatch = true;
                    break;
                  }
                }
              } catch {}
            }
          }
        }
      } catch {}
    }
    
    return emailMatch || walletMatch || propertyMatch || propEmailMatch || propWalletMatch || fallbackMatch;
  });
    const totalEarnings = eligibleBookings.reduce((sum: number, b: Booking) => sum + b.totalPrice, 0);
    
    const pendingBookings = hostBookings.filter((b: Booking) => b.status === 'pending').length;

    setStats({
      totalProperties: userProperties.length,
      totalBookings: eligibleBookings.length,
      totalEarnings,
      pendingBookings
    });
  };

  const handlePropertySubmit = (propertyData: PropertyFormData) => {
    const newProperty: Property = {
      id: `property_${Date.now()}`,
      name: propertyData.name,
      description: propertyData.description,
      location: propertyData.location,
      price: propertyData.price,
      amenities: propertyData.amenities,
      imageUrl: propertyData.imageUrl || 'https://via.placeholder.com/400x300?text=No+Image',
      hostEmail: userEmail,
      hostWallet: propertyData.hostWallet || userWallet,
      contactDetails: propertyData.contactDetails
    };

    // Attempt on-chain mint (best-effort, non-blocking)
    (async () => {
      try {
        const metadata = makeDataUriJson({
          name: newProperty.name,
          description: newProperty.description,
          image: newProperty.imageUrl,
          attributes: [
            { trait_type: 'Location', value: newProperty.location },
            { trait_type: 'PricePerNightHBAR', value: newProperty.price }
          ]
        });
        const res = await mintPropertyNft(newProperty.hostWallet || userWallet, newProperty.id, metadata);
        if (res) {
          (newProperty as any).nftContract = (localStorage.getItem('afg_contracts') ? JSON.parse(localStorage.getItem('afg_contracts') as string).registry : '') || '';
          (newProperty as any).nftTokenId = res.tokenId;
          (newProperty as any).nftTx = res.tx;
        }
      } catch (e) {
        console.warn('Mint property NFT skipped:', e);
      }
    })();

    // Save under per-host key for durability across login methods
    const hostId = (userEmail || userWallet || '').toLowerCase();
    const perHostKey = `afg_host_properties_${hostId}`;
    const perHostList = JSON.parse(localStorage.getItem(perHostKey) || '[]');
    perHostList.push(newProperty);
    localStorage.setItem(perHostKey, JSON.stringify(perHostList));

    // Also append to legacy global list for backward compatibility and guest merge
    const storedProperties = JSON.parse(localStorage.getItem('ethereus_host_properties') || '[]');
    storedProperties.push(newProperty);
    localStorage.setItem('ethereus_host_properties', JSON.stringify(storedProperties));

    setHostProperties(prev => [...prev, newProperty]);
    setShowListingForm(false);
    setActiveTab('properties');
    // Notify other parts of the app (same tab) to refresh guest listings immediately
    try { window.dispatchEvent(new Event('afg:properties-updated')); } catch {}
    
    alert('Property listed successfully!');
  };

  const handleBookingStatusChange = (bookingId: string, status: 'confirmed' | 'cancelled') => {
    const storedBookings = JSON.parse(localStorage.getItem('ethereus_bookings') || '[]');
    const updatedBookings = storedBookings.map((booking: Booking) =>
      booking.id === bookingId ? { ...booking, status } : booking
    );
    localStorage.setItem('ethereus_bookings', JSON.stringify(updatedBookings));
    
    loadHostData(); // Reload data to update stats
    try { window.dispatchEvent(new Event('afg:bookings-updated')); } catch {}
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatPrice = (price: number) => {
    return `${price} HBAR`;
  };

  const getUnreadCount = (bookingId: string): number => {
    const key = `afg_chat_${bookingId}`;
    const msgs: Array<{ at: number }> = JSON.parse(localStorage.getItem(key) || '[]');
    const rk = `afg_chat_read_host_${bookingId}`;
    const lastRead = Number(localStorage.getItem(rk) || '0');
    return msgs.filter(m => (m.at || 0) > lastRead).length;
  };

  return (
    <div className="host-dashboard">
      <div className="dashboard-header">
        <h1>Host Dashboard</h1>
        <p>Welcome back! Manage your properties and bookings.</p>
      </div>
      {availabilityProperty && (
        <div className="property-modal-overlay" onClick={() => setAvailabilityProperty(null)}>
          <div className="property-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content" style={{ gridTemplateColumns: '1fr' }}>
              <div className="modal-details">
                <h2>Block Dates — {availabilityProperty.name}</h2>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input id="afg-block-date" type="date" />
                  <button className="confirm-btn" onClick={() => {
                    const el = document.getElementById('afg-block-date') as HTMLInputElement;
                    if (el && el.value) {
                      const key = `afg_blocked_${availabilityProperty.id}`;
                      const dates = Array.from(new Set([...(JSON.parse(localStorage.getItem(key) || '[]')), el.value]));
                      localStorage.setItem(key, JSON.stringify(dates));
                      setBlockedDates(dates);
                    }
                  }}>Add</button>
                </div>
                <div style={{ marginTop: 12 }}>
                  {blockedDates.length === 0 && <p>No blocked dates</p>}
                  {blockedDates.map(d => (
                    <div key={d} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span>{d}</span>
                      <button className="cancel-btn" onClick={() => {
                        const key = `afg_blocked_${availabilityProperty.id}`;
                        const dates = (JSON.parse(localStorage.getItem(key) || '[]')).filter((x: string) => x !== d);
                        localStorage.setItem(key, JSON.stringify(dates));
                        setBlockedDates(dates);
                      }}>Remove</button>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 12 }}>
                  <button className="cta-button" onClick={() => setAvailabilityProperty(null)}>Done</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {chatBooking && (
        <div className="property-modal-overlay" onClick={() => setChatBooking(null)}>
          <div className="property-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content" style={{ gridTemplateColumns: '1fr' }}>
              <div className="modal-details">
                <h2>Chat — {chatBooking.propertyName}</h2>
                <div style={{ maxHeight: 260, overflow: 'auto', background: '#f7fafc', padding: 12, borderRadius: 8 }}>
                  {chatMessages.length === 0 && <p style={{ color: '#718096' }}>No messages yet</p>}
                  {chatMessages.map((m, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: m.sender === 'host' ? 'flex-end' : 'flex-start', marginBottom: 6 }}>
                      <span style={{ background: m.sender === 'host' ? '#c6f6d5' : '#e2e8f0', color: '#000000', padding: '6px 10px', borderRadius: 12, fontWeight: 500 }}>
                        {m.text}
                      </span>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <input type="text" placeholder="Type a message" style={{ flex: 1 }} onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const input = e.currentTarget as HTMLInputElement;
                      const key = `afg_chat_${chatBooking.id}`;
                      const msgs = JSON.parse(localStorage.getItem(key) || '[]');
                      msgs.push({ sender: 'host', text: input.value, at: Date.now() });
                      localStorage.setItem(key, JSON.stringify(msgs));
                      setChatMessages(msgs);
                      try {
                        const ch = new (window as any).BroadcastChannel('afg-chat');
                        ch.postMessage({ type: 'chat', bookingKey: key, message: { sender: 'host', text: input.value, at: Date.now() }, isLocal: true });
                        setTimeout(() => ch.close(), 100);
                      } catch {}
                      input.value = '';
                    }
                  }} />
                  <button className="confirm-btn" onClick={() => {
                    const inputs = document.querySelectorAll<HTMLInputElement>('input[placeholder="Type a message"]');
                    const el = inputs[inputs.length - 1];
                    if (el && el.value) {
                      const key = `afg_chat_${chatBooking.id}`;
                      const msgs = JSON.parse(localStorage.getItem(key) || '[]');
                      msgs.push({ sender: 'host', text: el.value, at: Date.now() });
                      localStorage.setItem(key, JSON.stringify(msgs));
                      setChatMessages(msgs);
                      try {
                        const ch = new (window as any).BroadcastChannel('afg-chat');
                        ch.postMessage({ type: 'chat', bookingKey: key, message: { sender: 'host', text: el.value, at: Date.now() }, isLocal: true });
                        setTimeout(() => ch.close(), 100);
                      } catch {}
                      el.value = '';
                    }
                  }}>Send</button>
                </div>
                <div style={{ marginTop: 12 }}>
                  <button className="cta-button" onClick={() => setChatBooking(null)}>Close</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="dashboard-tabs">
        <button
          className={`tab-button ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          📊 Dashboard
        </button>
        <button
          className={`tab-button ${activeTab === 'properties' ? 'active' : ''}`}
          onClick={() => setActiveTab('properties')}
        >
          🏠 Properties
        </button>
        <button
          className={`tab-button ${activeTab === 'bookings' ? 'active' : ''}`}
          onClick={() => setActiveTab('bookings')}
        >
          📅 Bookings
        </button>
        <button
          className={`tab-button ${activeTab === 'list-property' ? 'active' : ''}`}
          onClick={() => setActiveTab('list-property')}
        >
          ➕ List Property
        </button>
      </div>

      <div className="dashboard-content">
        {activeTab === 'dashboard' && (
          <div className="dashboard-overview">
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">🏠</div>
                <div className="stat-content">
                  <h3>{stats.totalProperties}</h3>
                  <p>Total Properties</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">📅</div>
                <div className="stat-content">
                  <h3>{stats.totalBookings}</h3>
                  <p>Total Bookings</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">💰</div>
                <div className="stat-content">
                  <h3>{formatPrice(stats.totalEarnings)}</h3>
                  <p>Total Earnings</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">⏳</div>
                <div className="stat-content">
                  <h3>{stats.pendingBookings}</h3>
                  <p>Pending Bookings</p>
                </div>
              </div>
            </div>

            <div className="recent-bookings">
              <h3>Recent Bookings</h3>
              {bookings.slice(0, 5).map(booking => (
                <div key={booking.id} className="booking-item">
                  <div className="booking-info">
                    <h4>{booking.propertyName}</h4>
                    <p>{booking.guestName} • {formatDate(booking.startDate)} - {formatDate(booking.endDate)}</p>
                  </div>
                  <div className="booking-status">
                    <span className={`status-badge ${booking.status}`}>
                      {booking.status}
                    </span>
                    <span className="booking-price">{formatPrice(booking.totalPrice)}</span>
                  </div>
                </div>
              ))}
              {bookings.length === 0 && (
                <p className="no-data">No bookings yet</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'properties' && (
          <div className="properties-section">
            <div className="section-header">
              <h3>Your Properties</h3>
              <button
                className="add-property-btn"
                onClick={() => setActiveTab('list-property')}
              >
                Add Property
              </button>
            </div>
            
            <div className="properties-grid">
              {hostProperties.map(property => (
                <div key={property.id} className="property-card">
                  <img src={property.imageUrl} alt={property.name} />
                  <div className="property-info">
                    <h4>{property.name}</h4>
                    <p>{property.location}</p>
                    <div className="property-price">{formatPrice(property.price)}/night</div>
                    <div className="property-amenities">
                      {property.amenities.slice(0, 3).map((amenity, index) => (
                        <span key={index} className="amenity-tag">{amenity}</span>
                      ))}
                    </div>
                    <div style={{ marginTop: 12 }}>
                      <button
                        className="add-property-btn"
                        onClick={() => {
                          setAvailabilityProperty(property);
                          const dates = JSON.parse(localStorage.getItem(`afg_blocked_${property.id}`) || '[]');
                          setBlockedDates(dates);
                        }}
                      >
                        Manage Availability
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {hostProperties.length === 0 && (
              <div className="no-properties">
                <h3>No properties listed yet</h3>
                <p>Start by listing your first property to begin earning!</p>
                <button
                  className="cta-button"
                  onClick={() => setActiveTab('list-property')}
                >
                  List Your First Property
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'bookings' && (
          <div className="bookings-section">
            <h3>Booking Management</h3>
            
            <div className="bookings-list">
              {bookings.map(booking => (
                <div key={booking.id} className="booking-card">
                  <div className="booking-header">
                    <h4>{booking.propertyName}</h4>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className={`status-badge ${booking.status}`}>
                      {booking.status}
                    </span>
                      {getUnreadCount(booking.id) > 0 && (
                        <span className="status-badge" style={{ background: '#c6f6d5', color: '#22543d' }}>
                          {getUnreadCount(booking.id)} new
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="booking-details">
                    <div className="guest-info">
                      <p><strong>Guest:</strong> {booking.guestName}</p>
                      <p><strong>Email:</strong> {booking.guestEmail}</p>
                      <p><strong>Wallet:</strong> {booking.guestWallet.slice(0, 10)}...</p>
                    </div>
                    
                    <div className="booking-dates">
                      <p><strong>Check-in:</strong> {formatDate(booking.startDate)}</p>
                      <p><strong>Check-out:</strong> {formatDate(booking.endDate)}</p>
                      <p><strong>Total:</strong> {formatPrice(booking.totalPrice)}</p>
                    </div>
                  </div>
                  
                  <div className="booking-actions">
                  {booking.status === 'pending' && (
                      <>
                      <button
                        className="confirm-btn"
                        onClick={() => handleBookingStatusChange(booking.id, 'confirmed')}
                      >
                        Confirm Booking
                      </button>
                      <button
                        className="cancel-btn"
                        onClick={() => handleBookingStatusChange(booking.id, 'cancelled')}
                      >
                        Cancel Booking
                        </button>
                      </>
                    )}
                    <button
                      className="add-property-btn"
                      onClick={() => {
                        setChatBooking(booking);
                        const msgs = JSON.parse(localStorage.getItem(`afg_chat_${booking.id}`) || '[]');
                        setChatMessages(msgs);
                        localStorage.setItem(`afg_chat_read_host_${booking.id}`, String(Date.now()));
                      }}
                    >
                      Chat
                      </button>
                    </div>
                </div>
              ))}
              
              {bookings.length === 0 && (
                <p className="no-data">No bookings yet</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'list-property' && (
          <div className="list-property-section">
            <PropertyListingForm
              onSubmit={handlePropertySubmit}
              onCancel={() => setActiveTab('properties')}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default HostDashboard;
